import {
  type AppleCredentials,
  type AppleLoginErrorResponse,
  type InvalidLoginResponse,
  type MfaRequiredErrorResponse,
} from './aliases';

export type LoginCredentials =
  | MagicLinkCredentials
  | OobGrantCredentials
  | GoogleGrantCredentials
  | AppleCredentials;

export type SSOCredentials = AppleCredentials | GoogleGrantCredentials;

export type SSOSignupCredentials = AppleCredentials | GoogleSignupCredential;

export enum MagicLinkType {
  LOGIN = 'login',
  SIGNUP = 'signup',
}

export type MagicLinkCredentials = {
  magic_link_token: string;
};

export type OobGrantCredentials = {
  mfa_token: string;
  challenge_id: string;
  binding_code: string;
};

export type GoogleGrantCredentials = {
  google_id_token: string;
};

export enum MFALoginErrorType {
  ChallengeCountExceeded = 'MFA_CHALLENGE_COUNT_EXCEEDED',
  ChallengeNotFound = 'MFA_CHALLENGE_NOT_FOUND',
}

export type CombinedLoginErrorResponse =
  AppleLoginErrorResponse | InvalidLoginResponse | MfaRequiredErrorResponse;

export enum LoginType {
  Facebook = 'facebook',
  Google = 'google',
  Apple = 'apple',
  MagicLink = 'magicLink',
  AppleSignup = 'apple_signup',
  GoogleSignup = 'google_signup',
}

export type GoogleSignupCredential = {
  googleIdToken: string;
};

export type AccountLinkingCredential =
  | GoogleSignupCredential
  | {
      appleId: string;
      apple_access_token: string;
    }
  | undefined;

export function isGoogleGrantCredential(
  credential: LoginCredentials
): credential is GoogleGrantCredentials {
  if (
    typeof credential === 'object' &&
    Object.hasOwn(credential, 'google_id_token')
  ) {
    return true;
  }

  return false;
}

export function isAppleCredentials(
  credential: LoginCredentials | SSOSignupCredentials
): credential is AppleCredentials {
  if (
    typeof credential === 'object' &&
    Object.hasOwn(credential, 'apple_access_token')
  ) {
    return true;
  }

  return false;
}

export function isLoginError(
  error: unknown
): error is CombinedLoginErrorResponse {
  if (typeof error !== 'object' || error === null) {
    return false;
  }

  const errorKeys = new Set(Object.keys(error));

  return [
    'code',
    'error',
    'message',
    'id',
    'detail',
    'error_message',
    'error_description',
  ].every((field) => errorKeys.has(field));
}

export enum RecoveryOptions {
  RESEND = 'resend',
  RECOVERY = 'recovery',
  HELP = 'help',
}

export type ErrorMessage = { error: string; error_message: string };
